import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Printer, Search, ChevronDown, ChevronRight, TrendingUp, TrendingDown,
  Minus, ArrowRight, AlertTriangle, Package, RefreshCw, Eye, EyeOff, Receipt, Send
} from 'lucide-react';
import SendToPrintModal from '../components/SendToPrintModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import PrintEngine from '../lib/PrintEngine';

const formatPHP = (v) => `₱${(parseFloat(v) || 0).toLocaleString('en-PH', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const RANGES = [
  { value: '1d',   label: '1 day' },
  { value: '7d',   label: '1 week' },
  { value: '30d',  label: 'Last 30 days' },
  { value: '60d',  label: 'Last 60 days' },
  { value: '90d',  label: 'Quarter (90d)' },
  { value: '180d', label: '6 Months' },
  { value: '365d', label: 'Year' },
  { value: 'all',  label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const MONTH_LABEL = (yyyymm) => {
  if (!yyyymm || yyyymm.length < 7) return yyyymm;
  const [y, m] = yyyymm.split('-');
  const date = new Date(parseInt(y), parseInt(m) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const FMT_DAY = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
    // Year only when it isn't the current one — a stale date announces its age.
    const opts = d.getFullYear() !== new Date().getFullYear()
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' };
    return d.toLocaleDateString('en-US', opts);
  } catch { return ''; }
};

// The date a change ACTUALLY happened: the DR day (labelled) when the receipt
// has one, else the encode day. Falls back to changed_at for cached responses
// from before the `date` field existed.
const CH_DATE = (ch) => {
  const d = ch.date || ch.changed_at;
  return `${ch.date_is_dr ? 'DR ' : ''}${FMT_DAY(d)}`;
};

// ── Tiny inline change "pill" ────────────────────────────────────────────────
function ChangePill({ ch, showOldFirst = false }) {
  const cls = ch.direction === 'up'
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : ch.direction === 'down'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  const Icon = ch.direction === 'up' ? TrendingUp
    : ch.direction === 'down' ? TrendingDown : Minus;
  const title = [
    CH_DATE(ch),
    ch.date_is_dr && (ch.date !== (ch.changed_at || '').slice(0, 10)) && `· encoded ${FMT_DAY(ch.changed_at)}`,
    ch.source_ref && `· ${ch.source_ref}`,
    ch.vendor && `· ${ch.vendor}`,
    ch.branch_name && `· ${ch.branch_name}`,
  ].filter(Boolean).join(' ');
  return (
    <span
      data-testid="capital-change-pill"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${cls}`}
      title={title}
    >
      <Icon className="w-3 h-3" />
      {showOldFirst && (
        <>
          <span className="opacity-60 line-through">{formatPHP(ch.old_capital)}</span>
          <ArrowRight className="w-3 h-3 opacity-50" />
        </>
      )}
      <span>{formatPHP(ch.new_capital)}</span>
      {ch.delta_pct !== null && ch.delta_pct !== undefined && (
        <span className="opacity-70 text-[10px]">
          ({ch.delta_pct > 0 ? '+' : ''}{ch.delta_pct}%)
        </span>
      )}
      {/* Both tracks on every event (owner ask 2026-09-02): the big number is
          the CAPITAL outcome; the badge says which method decided it; the
          other track rides alongside whenever it differs — so an MA-cushioned
          event still shows the real receipt price, and an LP event shows the
          MA it declined to use (when the row recorded one). */}
      {ch.method === 'moving_average' && (
        <>
          <span className="text-[9px] font-bold px-1 rounded bg-white/70 border border-current opacity-80">MA</span>
          {ch.lp > 0 && Math.abs(ch.lp - ch.new_capital) > 0.005 && (
            <span className="opacity-70 text-[10px]">LP {formatPHP(ch.lp)}</span>
          )}
        </>
      )}
      {ch.method === 'last_purchase' && (
        <>
          <span className="text-[9px] font-bold px-1 rounded bg-white/70 border border-current opacity-80">LP</span>
          {ch.ma > 0 && Math.abs(ch.ma - ch.new_capital) > 0.005 && (
            <span className="opacity-70 text-[10px]">MA {formatPHP(ch.ma)}</span>
          )}
        </>
      )}
    </span>
  );
}

// Calming-green easy-view hover band vs the default faint hover
const ROW_HOVER = (easyView) =>
  easyView
    ? 'hover:bg-emerald-100/70 hover:border-l-2 hover:border-l-emerald-400 transition-colors'
    : 'hover:bg-slate-50/40';

// ── Sequence View: starting capital → c1 → c2 → ... → current ────────────────
function SequenceRow({ p, easyView }) {
  return (
    <tr className={`border-t border-slate-100 align-top break-inside-avoid ${ROW_HOVER(easyView)}`}>
      <td className="py-2 pr-3 text-sm font-medium text-slate-800 w-[26%]">
        <div>{p.name}</div>
        <div className="text-[11px] text-slate-400 font-normal">
          {p.sku && <span>SKU {p.sku}</span>}
          {p.unit && <span> · {p.unit}</span>}
          {p.is_repack && <span className="ml-1 text-amber-600">· repack</span>}
        </div>
      </td>
      <td className="py-2 pr-3">
        {/* MA track — the moving-average / effective costing basis over time */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[9px] font-bold text-blue-500 w-5 shrink-0">MA</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
            {formatPHP(p.start_ma ?? p.first_capital)}
            <span className="ml-1 text-[10px] opacity-60">start</span>
          </span>
          {p.changes.map((ch, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <ArrowRight className="w-3 h-3 text-slate-400" />
              <ChangePill ch={ch} />
            </span>
          ))}
        </div>
        {/* LP track — the actual purchase (last-purchase) prices over time */}
        {(p.start_lp != null || p.changes.some(c => c.lp > 0)) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <span className="text-[9px] font-bold text-slate-400 w-5 shrink-0">LP</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-50 text-slate-500 border border-slate-200">
              {formatPHP(p.start_lp ?? p.first_capital)}
              <span className="ml-1 text-[10px] opacity-60">start</span>
            </span>
            {p.changes.filter(c => c.lp > 0).map((ch, i) => (
              <span key={i} className="inline-flex items-center gap-1.5">
                <ArrowRight className="w-3 h-3 text-slate-300" />
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium bg-slate-50 text-slate-600 border-slate-200"
                  title={`Purchase price · ${CH_DATE(ch)}${ch.source_ref ? ' · ' + ch.source_ref : ''}`}
                >
                  {formatPHP(ch.lp)}
                </span>
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="py-2 pr-3 text-right whitespace-nowrap w-[14%]">
        <div className={`text-sm font-semibold ${
          p.net_delta > 0 ? 'text-rose-600'
          : p.net_delta < 0 ? 'text-emerald-600' : 'text-slate-500'
        }`}>
          {p.net_delta > 0 ? '+' : ''}{formatPHP(p.net_delta)}
        </div>
        {p.net_delta_pct !== null && p.net_delta_pct !== undefined && (
          <div className="text-[11px] text-slate-400">
            {p.net_delta_pct > 0 ? '+' : ''}{p.net_delta_pct}%
          </div>
        )}
      </td>
      <td className="py-2 text-right whitespace-nowrap w-[12%] text-sm">
        <div className="font-bold text-slate-800">
          {formatPHP(p.current_ma ?? p.current_capital)}
          <span className="ml-1 text-[9px] text-blue-500 font-bold">MA</span>
        </div>
        {p.current_lp != null && (
          <div className="text-[11px] text-slate-500">
            {formatPHP(p.current_lp)}
            <span className="ml-1 text-[9px] text-slate-400 font-bold">LP</span>
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Month-bucket View: one column per month, multiple changes per cell ───────
function MonthBucketTable({ products, months, easyView }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-3 w-[24%]">Product</th>
            <th className="py-2 pr-3 whitespace-nowrap">Start</th>
            {months.map(m => (
              <th key={m} className="py-2 pr-3 whitespace-nowrap">{MONTH_LABEL(m)}</th>
            ))}
            <th className="py-2 pr-3 text-right whitespace-nowrap">Current</th>
            <th className="py-2 text-right whitespace-nowrap">Net</th>
          </tr>
        </thead>
        <tbody>
          {products.map(p => (
            <tr key={p.product_id} className={`border-t border-slate-100 align-top break-inside-avoid ${ROW_HOVER(easyView)}`}>
              <td className="py-2 pr-3 font-medium text-slate-800">
                <div>{p.name}</div>
                <div className="text-[11px] text-slate-400 font-normal">
                  {p.sku && <span>SKU {p.sku}</span>}
                  {p.unit && <span> · {p.unit}</span>}
                </div>
              </td>
              <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                <div><span className="text-[9px] text-blue-500 font-bold mr-1">MA</span>{formatPHP(p.start_ma ?? p.first_capital)}</div>
                {p.start_lp != null && (
                  <div className="text-[11px] text-slate-400"><span className="text-[9px] font-bold mr-1">LP</span>{formatPHP(p.start_lp)}</div>
                )}
              </td>
              {months.map(m => {
                const cell = p.by_month?.[m] || [];
                if (!cell.length) {
                  return <td key={m} className="py-2 pr-3 text-slate-300">—</td>;
                }
                return (
                  <td key={m} className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {cell.map((ch, i) => <ChangePill key={i} ch={ch} />)}
                    </div>
                  </td>
                );
              })}
              <td className="py-2 pr-3 text-right whitespace-nowrap">
                <div className="font-bold text-slate-800">
                  {formatPHP(p.current_ma ?? p.current_capital)}
                  <span className="ml-1 text-[9px] text-blue-500 font-bold">MA</span>
                </div>
                {p.current_lp != null && (
                  <div className="text-[11px] text-slate-500">
                    {formatPHP(p.current_lp)}
                    <span className="ml-1 text-[9px] text-slate-400 font-bold">LP</span>
                  </div>
                )}
              </td>
              <td className="py-2 text-right whitespace-nowrap">
                <span className={`text-sm font-semibold ${
                  p.net_delta > 0 ? 'text-rose-600'
                  : p.net_delta < 0 ? 'text-emerald-600' : 'text-slate-500'
                }`}>
                  {p.net_delta > 0 ? '+' : ''}{formatPHP(p.net_delta)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Print dialog (same shape as the /sales-new "Order Slip Generated" card,
// owner 2026-09-03): the two local papers side by side, then one full-width
// "Send to Branch Printer". Each paper has exactly one layout — dot matrix =
// the sheet as shown, thermal = a one-category price strip — so the thermal
// button opens the category preview (the length guard) before anything prints.
const STRIP_LONG = 80;
function CapitalPrintDialog({
  open, onOpenChange, category, onCategoryChange, categories, params, bizInfo,
  sheet, onPrintSheet, onSendToBranch, sending, onWholeCategoryChange,
}) {
  const [thermalOpen, setThermalOpen] = useState(false);
  // Default = only products with a receipt in the period (what the screen
  // shows). The whole-category "as of" list is opt-in — Feeds printed 8 pages
  // that way (owner 2026-09-03).
  const [wholeCategory, setWholeCategory] = useState(false);
  const [strip, setStrip] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setThermalOpen(false); setWholeCategory(false); } }, [open]);
  useEffect(() => {
    if (!open || !category) { setStrip(null); return undefined; }
    let alive = true;
    setLoading(true);
    api.get('/products/capital-change-report/price-strip', { params: { ...params, category, only_changed: !wholeCategory } })
      .then(r => { if (alive) setStrip(r.data); })
      .catch(e => {
        if (!alive) return;
        setStrip(null);
        toast.error(e?.response?.data?.detail || 'Failed to load the price strip');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, category, params, wholeCategory]);
  useEffect(() => { if (onWholeCategoryChange) onWholeCategoryChange(wholeCategory); }, [wholeCategory, onWholeCategoryChange]);

  const items = strip?.items || [];
  const long = items.length > STRIP_LONG;
  const stampOf = (it) => {
    const lc = it.last_change;
    if (!lc) return { text: 'no receipt', cls: 'text-slate-400' };
    if (lc.in_range) return { text: CH_DATE(lc), cls: 'text-slate-700' };
    return { text: `as of ${FMT_DAY(lc.date)}`, cls: 'text-amber-700' };
  };
  const printSheetHere = () => { onPrintSheet(); onOpenChange(false); };
  const printStripHere = () => {
    if (!strip || !items.length) { toast.error('Nothing to print for this category'); return; }
    PrintEngine.print({ type: 'capital_price_strip', data: strip, format: 'thermal', businessInfo: bizInfo });
    onOpenChange(false);
  };
  const stripReady = !loading && items.length > 0;
  const canSend = (sheet.available || stripReady) && !sending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Cap to the viewport and scroll the dialog itself — on a phone the summary +
          two papers + the thermal panel outgrow the screen (owner worry: 40+ lines). */}
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="capital-print-dialog">
        <DialogHeader>
          <DialogTitle>Print Capital Change Report</DialogTitle>
          <DialogDescription className="sr-only">Print this report here or send it to a branch printer.</DialogDescription>
        </DialogHeader>

        {/* What the sheet holds — the report exactly as shown on screen */}
        <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-center" data-testid="capital-print-sheet-info">
          <div className="text-sm font-semibold text-slate-800">{sheet.viewLabel} view · {sheet.rangeLabel}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {sheet.available
              ? `${sheet.products} product${sheet.products !== 1 ? 's' : ''} in ${sheet.categories} categor${sheet.categories !== 1 ? 'ies' : 'y'}${sheet.filters ? ` · ${sheet.filters}` : ''}`
              : 'No capital changes in this window — the sheet is empty'}
          </div>
        </div>

        <div className="text-sm font-medium text-slate-700">Print this report?</div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="h-12 justify-center"
            onClick={printSheetHere}
            disabled={!sheet.available}
            title="The report as shown, on the dot matrix printer"
            data-testid="capital-print-dot-matrix-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            8.5×11 Dot Matrix
          </Button>
          <Button
            variant="outline"
            className={`h-12 justify-center ${thermalOpen ? 'border-emerald-500 bg-emerald-50' : ''}`}
            onClick={() => setThermalOpen(o => !o)}
            title="A one-category price strip on the thermal printer"
            data-testid="capital-print-thermal-btn"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Thermal Print
            {thermalOpen ? <ChevronDown className="w-4 h-4 ml-2 opacity-60" /> : <ChevronRight className="w-4 h-4 ml-2 opacity-60" />}
          </Button>
        </div>

        {thermalOpen && (
          <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="capital-print-thermal-panel">
            <div className="flex items-end gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-[11px] uppercase tracking-wide text-slate-500">Category — one per strip</label>
                <Select value={category || ''} onValueChange={onCategoryChange}>
                  <SelectTrigger className="bg-white" data-testid="capital-strip-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-slate-500 pb-2 text-right" data-testid="capital-strip-count">
                {loading ? 'Loading…' : strip
                  ? (wholeCategory
                      ? `${items.length} product${items.length !== 1 ? 's' : ''} · ${strip.in_range_count} with a receipt in period`
                      : `${items.length} product${items.length !== 1 ? 's' : ''} changed in period`)
                  : ''}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-emerald-600"
                checked={wholeCategory}
                onChange={(e) => setWholeCategory(e.target.checked)}
                data-testid="capital-strip-whole-category"
              />
              Include every product in the category ("as of" newest receipt for the unchanged)
            </label>
            {long && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="capital-strip-long">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Long strip: {items.length} lines. It prints in full. Pick a smaller category if you only need part of it.</span>
              </div>
            )}
            <div className="max-h-[28vh] sm:max-h-[32vh] overflow-y-auto rounded-md border border-slate-200 bg-white">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5">Product</th>
                    <th className="text-right px-2 py-1.5">MA</th>
                    <th className="text-right px-2 py-1.5 hidden sm:table-cell">LP</th>
                    <th className="text-right px-2 py-1.5">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && items.map(it => {
                    const st = stampOf(it);
                    return (
                      <tr key={it.product_id} className="border-t border-slate-100" data-testid="capital-strip-row">
                        <td className="px-2 py-1">
                          <div className="font-medium text-slate-800">{it.name}</div>
                          <div className="text-[10px] text-slate-400">{it.sku}{it.unit ? ` · ${it.unit}` : ''}</div>
                        </td>
                        <td className="px-2 py-1 text-right font-semibold whitespace-nowrap">{formatPHP(it.current_ma)}</td>
                        <td className="px-2 py-1 text-right whitespace-nowrap text-slate-600 hidden sm:table-cell">{formatPHP(it.current_lp)}</td>
                        <td className={`px-2 py-1 text-right whitespace-nowrap ${st.cls}`}>{st.text}</td>
                      </tr>
                    );
                  })}
                  {!loading && strip && !items.length && (
                    <tr><td colSpan={4} className="px-2 py-6 text-center text-slate-400">No active products in this category</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-500">
                Current MA and LP with the receipt date in the period.{wholeCategory ? ' "as of" = newest receipt on record.' : ''}
              </div>
              <Button
                size="sm"
                onClick={printStripHere}
                disabled={!stripReady}
                className="bg-slate-900 hover:bg-slate-800 shrink-0"
                data-testid="capital-strip-print-btn"
              >
                <Receipt className="w-4 h-4 mr-1.5" />
                Print{items.length ? ` ${items.length} lines` : ''}
              </Button>
            </div>
          </div>
        )}

        <Button
          variant="outline"
          className="w-full h-12 border-emerald-500 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
          onClick={() => onSendToBranch({ stripReady })}
          disabled={!canSend}
          title="Queue it on a paired print station — the station modal lets you pick the paper"
          data-testid="capital-print-remote-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          {sending ? 'Preparing…' : 'Send to Branch Printer'}
        </Button>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Category Section (collapsible) ───────────────────────────────────────────
function CategorySection({ cat, viewMode, allMonths, easyView, onThermal }) {
  const [open, setOpen] = useState(true);
  const monthsInCat = useMemo(() => {
    const set = new Set();
    cat.products.forEach(p => Object.keys(p.by_month || {}).forEach(m => set.add(m)));
    return Array.from(set).sort();
  }, [cat]);
  const monthCols = allMonths.length ? allMonths : monthsInCat;
  return (
    <Card style={{ border: "1px solid var(--border-default)" }}>
      <div
        className="flex items-center justify-between px-4 py-3 bg-slate-50/60 border-b border-slate-200 cursor-pointer"
        onClick={() => setOpen(o => !o)}
        data-testid={`capital-category-header-${cat.category}`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <h3 className="text-sm font-semibold text-slate-800 tracking-wide">{cat.category}</h3>
          <Badge variant="outline" className="text-[10px] py-0 px-1.5">
            {cat.count} product{cat.count !== 1 ? 's' : ''}
          </Badge>
        </div>
        <button
          type="button"
          className="p-1 rounded text-slate-500 hover:bg-slate-200 hover:text-slate-800"
          title="Print a thermal price strip for this category"
          onClick={(e) => { e.stopPropagation(); onThermal(cat.category); }}
          data-testid={`capital-category-thermal-${cat.category}`}
        >
          <Receipt className="w-4 h-4" />
        </button>
      </div>

      {open && (
        <CardContent className="p-4">
          {viewMode === 'sequence' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 w-[26%]">Product</th>
                    <th className="py-2 pr-3">Cost progression</th>
                    <th className="py-2 pr-3 text-right w-[14%]">Net change</th>
                    <th className="py-2 text-right w-[10%]">Current</th>
                  </tr>
                </thead>
                <tbody>
                  {cat.products.map(p => <SequenceRow key={p.product_id} p={p} easyView={easyView} />)}
                </tbody>
              </table>
            </div>
          ) : (
            <MonthBucketTable products={cat.products} months={monthCols} easyView={easyView} />
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function CapitalChangeReportPage() {
  const { branches, selectedBranchId, canViewAllBranches } = useAuth();
  const [range, setRange] = useState('30d');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [category, setCategory] = useState('all');
  const [branchScope, setBranchScope] = useState(canViewAllBranches ? 'all' : (selectedBranchId || 'all'));
  const [sourceType, setSourceType] = useState('purchase_order');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('sequence');
  const [easyView, setEasyView] = useState(() => {
    try { return localStorage.getItem('ccr_easy_view') === '1'; } catch { return false; }
  });
  const toggleEasyView = () => {
    setEasyView(prev => {
      const next = !prev;
      try { localStorage.setItem('ccr_easy_view', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [bizInfo, setBizInfo] = useState({});

  // Print dialog: what (sheet | strip) then where (here | station). A strip is
  // one category, previewed before printing.
  const [printOpen, setPrintOpen] = useState(false);
  const [stripCategory, setStripCategory] = useState('');
  const [stripWholeCategory, setStripWholeCategory] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  useEffect(() => {
    api.get('/products/categories')
      .then(r => setAllCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);
  const stripParams = useMemo(() => ({
    range,
    source_type: sourceType,
    ...(range === 'custom' && dateFrom ? { date_from: dateFrom } : {}),
    ...(range === 'custom' && dateTo ? { date_to: dateTo } : {}),
    ...(branchScope && branchScope !== 'all' ? { branch_id: branchScope } : {}),
  }), [range, dateFrom, dateTo, sourceType, branchScope]);
  // Remote (station) print: freeze the report server-side into a snapshot,
  // then hand its id to the standard send-to-print modal. A strip pins the
  // modal to Thermal, the sheet to Dot Matrix — each has exactly one layout.
  const [remote, setRemote] = useState(null);   // { byFormat: { dot_matrix?: {documentId, documentName}, thermal?: {...} } }
  const [snapshotting, setSnapshotting] = useState(false);
  const remoteBranchId = (branchScope !== 'all' ? branchScope : selectedBranchId) || '';
  const snapshot = (body) => api.post('/products/capital-change-report/snapshot', { ...stripParams, ...body })
    .then(r => ({ documentId: r.data.snapshot_id, documentName: r.data.document_name }));
  // "Send to Branch Printer": freeze every paper that has something to print —
  // the sheet (dot matrix) when the window has changes, the strip (thermal) when
  // a category is loaded — and let the station modal pick between them.
  const sendToBranch = async ({ stripReady }) => {
    if (snapshotting) return;
    setSnapshotting(true);
    try {
      const byFormat = {};
      if (data?.categories?.length) {
        byFormat.dot_matrix = await snapshot({
          kind: 'sheet', view_mode: viewMode,
          ...(category !== 'all' ? { category } : {}),
          ...(search.trim() ? { search: search.trim() } : {}),
        });
      }
      if (stripReady && stripCategory) {
        byFormat.thermal = await snapshot({ kind: 'strip', category: stripCategory, only_changed: !stripWholeCategory });
      }
      if (!Object.keys(byFormat).length) { toast.error('Nothing to send'); return; }
      setPrintOpen(false);   // the station modal takes over — no stacked dialogs
      setRemote({ byFormat });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not prepare the report for the print station');
    } finally {
      setSnapshotting(false);
    }
  };
  const defaultStripCategory = (cat) => cat
    || (category !== 'all' ? category : '')
    || data?.categories?.[0]?.category
    || allCategories[0]
    || '';
  const openPrint = (cat) => {
    if (cat || !stripCategory) setStripCategory(defaultStripCategory(cat));
    setPrintOpen(true);
  };
  // What the sheet would print — shown in the dialog so the choice is informed.
  const sheetInfo = useMemo(() => {
    const rangeLabel = RANGES.find(r => r.value === range)?.label || range;
    const dates = range === 'custom' && (dateFrom || dateTo) ? ` ${dateFrom || '…'} to ${dateTo || '…'}` : '';
    const parts = [];
    if (category !== 'all') parts.push(`Category: ${category}`);
    if (branchScope !== 'all') parts.push(`Branch: ${branches.find(b => b.id === branchScope)?.name || branchScope}`);
    if (sourceType !== 'all') parts.push(`Source: ${sourceType.replace('_', ' ')}`);
    if (search.trim()) parts.push(`Search: "${search.trim()}"`);
    return {
      available: !!data?.categories?.length,
      viewLabel: viewMode === 'month' ? 'Month' : 'Sequence',
      rangeLabel: rangeLabel + dates,
      filters: parts.join(' · '),
      products: data?.summary?.products_affected || 0,
      categories: data?.summary?.categories_affected || 0,
    };
  }, [data, range, dateFrom, dateTo, category, branchScope, sourceType, search, viewMode, branches]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        range,
        source_type: sourceType,
      };
      if (range === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      if (category && category !== 'all') params.category = category;
      if (branchScope && branchScope !== 'all') params.branch_id = branchScope;
      if (search.trim()) params.search = search.trim();
      const { data: res } = await api.get('/products/capital-change-report', { params });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load capital change report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, dateFrom, dateTo, sourceType, category, branchScope, search]);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => { api.get('/settings/business-info').then(r => setBizInfo(r.data)).catch(() => {}); }, []);

  // Global month axis for the bucket view across all categories ---------------
  const allMonths = useMemo(() => {
    if (!data?.categories) return [];
    const set = new Set();
    data.categories.forEach(c =>
      c.products.forEach(p =>
        Object.keys(p.by_month || {}).forEach(m => set.add(m))));
    return Array.from(set).sort();
  }, [data]);

  const handlePrint = () => {
    if (!data || !data.categories?.length) {
      toast.error('No data to print');
      return;
    }
    // The sheet mirrors the screen: the current view (Sequence / Month grid)
    // and the branch NAME for the filter band (the API only carries the id).
    const branchName = branchScope !== 'all'
      ? (branches.find(b => b.id === branchScope)?.name || branchScope) : '';
    PrintEngine.print({
      type: 'capital_change_report',
      data: {
        ...data,
        view_mode: viewMode,
        filters: { ...(data.filters || {}), branch_name: branchName },
      },
      format: 'dot_matrix',
      businessInfo: bizInfo,
    });
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 max-w-[1400px] mx-auto">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Capital Change Report</h1>
          <p className="text-sm text-slate-500 mt-1">
            Products whose cost moved within the window. Use this to update your printed price list.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={easyView ? 'default' : 'outline'}
            size="sm"
            onClick={toggleEasyView}
            className={easyView ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            data-testid="capital-easy-view-toggle"
            title="Highlight the full row on hover for easier line reading"
          >
            {easyView ? <Eye className="w-4 h-4 mr-1.5" /> : <EyeOff className="w-4 h-4 mr-1.5" />}
            Easy View
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReport}
            data-testid="capital-report-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => openPrint()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="capital-report-print-btn"
            title="Print the sheet or a thermal strip, here or on a print station"
          >
            <Printer className="w-4 h-4 mr-1.5" />
            Print
          </Button>
        </div>
      </div>

      {/* ── Print Header (visible on paper only) ─────────────────────────── */}
      <div className="hidden mb-3">
        <h1 className="text-xl font-bold">Capital Change Report</h1>
        <div className="text-xs text-slate-600">
          Range: {RANGES.find(r => r.value === range)?.label}
          {category !== 'all' && ` · Category: ${category}`}
          {branchScope !== 'all' && ` · Branch: ${branches.find(b => b.id === branchScope)?.name || branchScope}`}
          {sourceType !== 'all' && ` · Source: ${sourceType.replace('_', ' ')}`}
          {` · Generated ${new Date().toLocaleString('en-US')}`}
        </div>
      </div>

      {/* ── Filter Bar ───────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Range</label>
            <div className="flex flex-wrap gap-1" data-testid="capital-range-tabs">
              {RANGES.map(r => (
                <button
                  key={r.value}
                  data-testid={`capital-range-${r.value}`}
                  onClick={() => setRange(r.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium border transition ${
                    range === r.value
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {range === 'custom' && (
              <div className="flex items-center gap-2 mt-2" data-testid="capital-custom-range">
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] uppercase tracking-wide text-slate-400">From</label>
                  <Input
                    type="date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-8 text-xs w-[150px]"
                    data-testid="capital-custom-date-from"
                  />
                </div>
                <span className="text-slate-400 text-xs mt-4">→</span>
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] uppercase tracking-wide text-slate-400">To</label>
                  <Input
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-8 text-xs w-[150px]"
                    data-testid="capital-custom-date-to"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="capital-category-filter">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {(data?.all_categories || []).map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canViewAllBranches && branches?.length > 1 && (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-slate-500">Branch</label>
              <Select value={branchScope} onValueChange={setBranchScope}>
                <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="capital-branch-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All branches</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Source</label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="capital-source-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="purchase_order">PO arrivals</SelectItem>
                <SelectItem value="manual_edit">Manual edits</SelectItem>
                <SelectItem value="all">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Search</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Product name or SKU..."
                className="h-8 text-xs pl-7"
                data-testid="capital-search-input"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wide text-slate-500">View</label>
            <div className="inline-flex bg-slate-100 rounded-md p-0.5 border border-slate-200">
              <button
                onClick={() => setViewMode('sequence')}
                className={`px-2.5 py-1 rounded text-xs font-medium ${
                  viewMode === 'sequence' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                }`}
                data-testid="capital-view-sequence"
              >
                Sequence
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-2.5 py-1 rounded text-xs font-medium ${
                  viewMode === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                }`}
                data-testid="capital-view-month"
              >
                Month
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Summary ─────────────────────────────────────────────────────── */}
      {data?.summary && (
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-slate-400" />
              <span className="text-slate-500">Products affected</span>
              <span className="font-bold text-slate-900" data-testid="capital-summary-products">
                {data.summary.products_affected}
              </span>
            </div>
            <div className="text-slate-300">·</div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Categories</span>
              <span className="font-bold text-slate-900">{data.summary.categories_affected}</span>
            </div>
            <div className="text-slate-300">·</div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Total changes</span>
              <span className="font-bold text-slate-900" data-testid="capital-summary-total">
                {data.summary.total_changes}
              </span>
            </div>
            <div className="text-slate-300">·</div>
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-rose-500" />
              <span className="text-rose-600 font-semibold">{data.summary.increases}</span>
              <span className="text-slate-500">increases</span>
            </div>
            <div className="flex items-center gap-1.5">
              <TrendingDown className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-emerald-600 font-semibold">{data.summary.decreases}</span>
              <span className="text-slate-500">decreases</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      {loading && (
        <Card><CardContent className="p-8 text-center text-sm text-slate-400">Loading…</CardContent></Card>
      )}

      {!loading && data && data.categories.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center">
            <AlertTriangle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-600 font-medium">No capital changes in this window</div>
            <div className="text-xs text-slate-400 mt-1">
              Try widening the time range or switching the source filter.
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && data && data.categories.length > 0 && (
        <div className="space-y-4" data-testid="capital-report-body">
          {data.categories.map(cat => (
            <CategorySection
              key={cat.category}
              cat={cat}
              viewMode={viewMode}
              allMonths={allMonths}
              easyView={easyView}
              onThermal={(cat) => openPrint(cat)}
            />
          ))}
        </div>
      )}

      <CapitalPrintDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        category={stripCategory}
        onCategoryChange={setStripCategory}
        categories={allCategories.length ? allCategories : (data?.all_categories || [])}
        params={stripParams}
        bizInfo={bizInfo}
        sheet={sheetInfo}
        onPrintSheet={handlePrint}
        onSendToBranch={sendToBranch}
        sending={snapshotting}
        onWholeCategoryChange={setStripWholeCategory}
      />

      {remote && (
        <SendToPrintModal
          open={!!remote}
          onOpenChange={(o) => { if (!o) setRemote(null); }}
          documentType="capital_change_report"
          receiverDocType="capital_change_report"
          documentByFormat={remote.byFormat}
          branchId={remoteBranchId}
          metadata={{ report: 'capital_change_report' }}
        />
      )}
    </div>
  );
}
